import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import StatusBadge from '../shared/StatusBadge';
import { chatMarkdownComponents } from '../shared/chatMarkdownComponents';
import { ChatMessage } from '../../types';
import { formatChatTime } from '../../utils/chatTimestamp';
import { normalizeAiMarkdown } from '../../utils/normalizeAiMarkdown';
import { prepareChatMarkdownForCharts } from '../../utils/normalizeChartMarkdown';

/**
 * Thai + short English labels for chat transcript badges (Team Discussion / Task inbox).
 * task_assignment = routing to device agents, not the final synthesized answer.
 */
export const MESSAGE_TYPE_DISPLAY: Record<string, string> = {
  task_assignment: 'มอบหมายให้ Agent อุปกรณ์',
  contribution: 'ขั้นตอน 1 · Contribution',
  discussion: 'ขั้นตอน 2 · Team reply',
  manager_result: 'สรุปจากผู้จัดการ',
  final_result: 'ผลลัพธ์',
  status: 'สถานะ',
  confirm_check: 'ยืนยันคำสั่ง · ตรวจสอบ',
  confirm_code: 'ยืนยันคำสั่ง · รหัส',
  confirm_verify: 'ยืนยันคำสั่ง · ตรวจสอบแล้ว',
  confirm_result: 'ยืนยันคำสั่ง · ผลลัพธ์',
  confirm_execute: 'ยืนยันคำสั่ง · ส่งคำสั่ง',
  master_routing: 'Master · กำหนดสถานี',
  manager_contribution: 'Manager · Contribution',
  manager_discussion: 'Manager · Discussion',
  master_final_result: 'Master · ผลรวม',
  employee_direct: 'คุย Device โดยตรง',
};

const TYPE_VARIANTS: Record<string, 'success' | 'error' | 'warning' | 'info' | 'muted'> = {
  task_assignment: 'info',
  contribution: 'success',
  discussion: 'warning',
  manager_result: 'info',
  final_result: 'success',
  status: 'muted',
  confirm_check: 'warning',
  confirm_code: 'warning',
  confirm_verify: 'info',
  confirm_result: 'success',
  confirm_execute: 'success',
  master_routing: 'info',
  manager_contribution: 'success',
  manager_discussion: 'warning',
  master_final_result: 'success',
  employee_direct: 'info',
};

const VARIANT_LEFT_BORDER: Record<string, string> = {
  success: 'border-l-app-success',
  error:   'border-l-app-error',
  warning: 'border-l-app-warning',
  info:    'border-l-app-accent',
  muted:   'border-l-app-border',
};

const VARIANT_BG: Record<string, string> = {
  success: 'bg-app-success/5',
  error:   'bg-app-error/5',
  warning: 'bg-app-warning/5',
  info:    'bg-app-accent/5',
  muted:   'bg-app-muted/40',
};

/** Resolve variant — detect failure in messages that are success-typed but carry error content. */
function resolveVariant(message: ChatMessage): 'success' | 'error' | 'warning' | 'info' | 'muted' {
  const base = TYPE_VARIANTS[message.message_type] || 'muted';
  if (base === 'success') {
    const c = message.content || '';
    if (c.includes('❌') || c.includes('ล้มเหลว') || c.includes('ไม่สามารถ')) return 'error';
  }
  return base;
}

interface MessageBubbleProps {
  message: ChatMessage;
  agentColor?: string;
}

const markdownProseClass =
  'prose prose-slate prose-sm max-w-none break-words ' +
  'prose-p:text-app-text-sec prose-p:leading-relaxed prose-p:my-2 prose-p:first:mt-0 ' +
  'prose-headings:text-app-text prose-headings:font-semibold prose-headings:scroll-mt-4 ' +
  'prose-h1:text-base prose-h2:text-base prose-h3:text-sm prose-h4:text-sm ' +
  'prose-headings:mt-4 prose-headings:mb-2 prose-headings:first:mt-0 ' +
  'prose-strong:text-app-text prose-strong:font-semibold ' +
  'prose-ul:my-2 prose-ul:text-app-text-sec prose-li:my-1 prose-li:leading-relaxed ' +
  'prose-li:marker:text-app-accent/80 ' +
  'prose-ol:my-2 prose-ol:text-app-text-sec ' +
  'prose-hr:border-app-border prose-hr:my-4 ' +
  'prose-blockquote:border-app-accent/40 prose-blockquote:text-app-text-sec prose-blockquote:italic ' +
  'prose-code:text-blue-600 dark:prose-code:text-blue-400 prose-code:bg-blue-50 dark:prose-code:bg-blue-900/30 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded-md prose-code:font-normal prose-code:before:content-none prose-code:after:content-none ' +
  'prose-pre:bg-app-muted prose-pre:border prose-pre:border-app-border prose-pre:rounded-lg prose-pre:text-app-text-sec ' +
  'prose-table:my-0';

export default function MessageBubble({ message, agentColor }: MessageBubbleProps) {
  const variant = resolveVariant(message);
  const body = prepareChatMarkdownForCharts(normalizeAiMarkdown(message.content || ''));
  const typeLabel =
    MESSAGE_TYPE_DISPLAY[message.message_type] ?? message.message_type ?? 'message';

  return (
    <div className="flex gap-3 py-2">
      <div
        className="w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-[11px] font-bold text-white shadow-sm mt-0.5"
        style={{ backgroundColor: agentColor || '#64748b' }}
      >
        {(message.agent_name || '?')[0].toUpperCase()}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
          <span className="text-sm font-semibold text-app-text">{message.agent_name}</span>
          <span title={message.message_type} className="inline-flex">
            <StatusBadge variant={variant}>{typeLabel}</StatusBadge>
          </span>
          <span className="text-[11px] text-app-text-dim ml-auto">
            {formatChatTime(message.timestamp)}
          </span>
        </div>
        <div
          className={`rounded-lg border border-app-border/60 border-l-2 ${VARIANT_LEFT_BORDER[variant]} ${VARIANT_BG[variant]} px-3.5 py-2.5`}
        >
          <div className={markdownProseClass}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm, remarkBreaks]}
              components={chatMarkdownComponents}
            >
              {body}
            </ReactMarkdown>
          </div>
        </div>
      </div>
    </div>
  );
}
